import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { DateTime } from "luxon";
import { getInviteDetails } from "../api/invitation";

const MeetingConfirmationPage = () => {
	const navigate = useNavigate();
	const location = useLocation();

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [invitation, setInvitation] = useState(null);

	const queryParams = new URLSearchParams(location.search);
	const invitationId = queryParams.get("id");

	useEffect(() => {
		if (!invitationId) {
			navigate("/");
			return;
		}
		(async () => {
			try {
				const invite = await getInviteDetails(invitationId);
				setInvitation(invite);
			} catch (err) {
				console.error("Error fetching invitation:", err);
				setError("Could not retrieve meeting details.");
			} finally {
				setLoading(false);
			}
		})();
	}, [invitationId, navigate]);

	const formatRange = slot => {
		// if (slot?.formatted) return slot.formatted;
		if (!slot?.start || !slot?.end) return "—";
		const start = DateTime.fromSeconds(slot.start);
		const end = DateTime.fromSeconds(slot.end);
		return (
			<>
				<div style={responsive.date}>{start.toFormat("cccc LLL d,")}</div>
				<div style={responsive.time}>
					{start.toFormat("h:mm a")} – {end.toFormat("h:mm a")}
				</div>
			</>
		);
	};

	const getGuestName = (invitee, inviteeEmail) => {
		if (invitee?.name) return invitee.name;
		if (inviteeEmail) {
			const base = inviteeEmail.split("@")[0].replace(/[._]/g, " ");
			return base
				.split(" ")
				.map(s => (s ? s[0].toUpperCase() + s.slice(1) : s))
				.join(" ");
		}
		return "Guest";
	};

	// ---------- responsive styles (same pattern as Dashboard) ----------
	const getResponsiveStyles = () => {
		const base = styles;

		if (window.innerWidth <= 480) {
			return {
				...base,
				shell: { ...base.shell, padding: "1.5rem 1rem" },
				card: { ...base.card, width: "92vw", padding: "1.25rem" },
				titleEmoji: { ...base.titleEmoji, fontSize: "40px", lineHeight: "1" },
				title: { ...base.title, fontSize: "28px", lineHeight: "1.1" },
				subtitle: { ...base.subtitle, fontSize: "14px", maxWidth: "90%" },
				date: { ...base.date },
				avatarLarge: { ...base.avatarLarge, width: "56px", height: "56px", fontSize: "22px" },
				avatarSmall: { ...base.avatarSmall, width: "40px", height: "40px", fontSize: "16px" },
			};
		} else if (window.innerWidth <= 768) {
			return {
				...base,
				shell: { ...base.shell, padding: "2rem" },
				card: { ...base.card, width: "420px", padding: "1.75rem" },
				title: { ...base.title, fontSize: "32px" },
			};
		} else if (window.innerWidth <= 1077) {
			return {
				...base,
				card: { ...base.card, width: "460px" },
			};
		}
		return base;
	};

	const responsive = getResponsiveStyles();

	if (loading) {
		return (
			<div style={responsive.page}>
				<div style={responsive.shell}>
					<div style={responsive.card}>
						<div style={{ ...responsive.centerRow, padding: "2rem 0" }}>
							<div className="spinner" style={responsive.spinner} />
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (error || !invitation) {
		return (
			<div style={responsive.page}>
				<div style={responsive.shell}>
					<div style={responsive.card}>
						<div style={{ textAlign: "center" }}>
							<h2 style={{ ...responsive.title, marginBottom: "0.5rem" }}>Something went wrong</h2>
							<p style={{ ...responsive.subtitle, margin: 0 }}>{error || "Meeting not found."}</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	const { inviter, invitee, inviteeEmail, selectedSlot } = invitation;
	const guestName = getGuestName(invitee, inviteeEmail);
	const guestEmail = invitee?.email || inviteeEmail || "—";

	const hostInitials =
		inviter?.name
			?.split(" ")
			.map(n => n[0])
			.join("")
			.toUpperCase() || "H";

	// const guestInitial = guestName && guestName[0] ? guestName[0].toUpperCase() : "G";

	return (
		<div style={responsive.page}>
			<div style={responsive.shell}>
				{/* Top brand (kept minimal, like Dashboard hierarchy) */}
				<div style={responsive.topBar}>
					<div style={responsive.brand}>Hyacinthe.io</div>
				</div>

				{/* Centered confirmation card */}
				<div style={responsive.centerWrap}>
					<div style={responsive.card}>
						<div style={{ textAlign: "center" }}>
							<div style={responsive.titleEmoji}>🎉</div>
							<h1 style={responsive.title}>
								Meeting
								<br />
								confirmed
							</h1>
							<p style={responsive.subtitle}>Your meeting has been successfully scheduled. Below are the details:</p>

							<div style={responsive.date}>{formatRange(selectedSlot)}</div>

							{/* Host */}
							<div style={{ textAlign: "center", marginTop: "1rem" }}>
								{inviter?.picture ? (
									<img
										src={inviter.picture}
										alt={inviter?.name || "Host"}
										style={{
											width: "56px",
											height: "56px",
											borderRadius: "50%",
											objectFit: "cover",
											marginBottom: "0.5rem",
										}}
									/>
								) : (
									<div
										style={{
											width: "56px",
											height: "56px",
											borderRadius: "50%",
											background: "#ccc",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											fontWeight: "bold",
											fontSize: "1.2rem",
											margin: "0 auto 0.5rem auto",
										}}>
										{hostInitials}
									</div>
								)}

								<div style={{ fontWeight: 800, marginBottom: "0.25rem" }}>{inviter?.name || "Host"}</div>
								<div style={{ opacity: 0.85, fontSize: "0.85rem" }}>{inviter?.email || "—"}</div>
							</div>

							{/* x separator */}
							<div style={responsive.xSep}>x</div>

							{/* Guest */}
							<div style={{ textAlign: "center", marginTop: "1rem" }}>
								<div style={{ fontWeight: 800, marginBottom: "0.25rem" }}>{guestName}</div>
								<div style={{ opacity: 0.85, fontSize: "0.85rem" }}>{guestEmail}</div>
							</div>

							<div style={responsive.hr} />

							<div style={responsive.footerNote}>See you soon 👋</div>
						</div>
					</div>
				</div>
			</div>

			{/* optional subtle vignette overlay to improve readability */}
			{/* <div style={responsive.vignette} /> */}
		</div>
	);
};

const styles = {
	page: {
		minHeight: "100vh",
		background: "url('./bg.jpg') center/cover no-repeat fixed",
		color: "white",
		overflowX: "hidden",
		fontFamily: "Montserrat, 'Open Sans', Arial, sans-serif",
		WebkitFontSmoothing: "antialiased",
		MozOsxFontSmoothing: "grayscale",
		position: "relative",
	},

	shell: {
		minHeight: "100vh",
		padding: "3rem",
		display: "flex",
		flexDirection: "column",
	},

	topBar: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		paddingBottom: "0.5rem",
	},

	brand: {
		fontSize: "22px",
		fontWeight: 700,
		lineHeight: "36px",
		margin: 0,
	},

	centerWrap: {
		// flex: 1,
		marginTop: "2rem",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},

	card: {
		width: "480px",
		maxWidth: "92vw",
		borderRadius: "16px",
		padding: "2rem",
		background: "linear-gradient(to right, rgba(216, 220, 244, 0.22), rgba(196, 214, 255, 0.22))",
		border: "1px solid rgba(255,255,255,0.12)",
		backdropFilter: "blur(10px)",
		boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
	},

	titleEmoji: {
		fontSize: "48px",
		lineHeight: 1,
		marginBottom: "0.25rem",
	},

	title: {
		fontSize: "36px",
		fontWeight: 700,
		lineHeight: 1.1,
		margin: "15px 0 15px",
	},

	subtitle: {
		fontSize: "15px",
		// opacity: 0.9,
		margin: "0 auto 5rem",
		maxWidth: "320px",
		lineHeight: 1.4,
	},

	date: {
		fontSize: "17px",
		fontWeight: "bold",
		marginBottom: "2px",
	},
	time: {
		fontSize: "12px",
		fontWeight: "400",
	},

	person: {
		display: "flex",
		alignItems: "center",
		gap: "0.75rem",
		justifyContent: "center",
	},

	personLines: {
		textAlign: "left",
	},

	personName: {
		fontSize: "14px",
		fontWeight: 800,
		lineHeight: 1.1,
	},

	personEmail: {
		fontSize: "12px",
		opacity: 0.85,
	},

	xSep: {
		fontWeight: 900,
		margin: "0.5rem 0 0.75rem",
	},

	avatarLargeImg: {
		width: "56px",
		height: "56px",
		borderRadius: "50%",
		objectFit: "cover",
		border: "2px solid rgba(255,255,255,0.2)",
	},

	avatarLarge: {
		width: "56px",
		height: "56px",
		borderRadius: "50%",
		background: "#f3f4f6",
		color: "#1f2937",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		fontWeight: 800,
		fontSize: "20px",
		border: "2px solid rgba(255,255,255,0.2)",
	},

	avatarSmall: {
		width: "40px",
		height: "40px",
		borderRadius: "50%",
		background: "#f3f4f6",
		color: "#1f2937",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		fontWeight: 800,
		fontSize: "14px",
		border: "2px solid rgba(255,255,255,0.2)",
	},

	hr: {
		height: "1px",
		background: "rgba(255,255,255,0.15)",
		margin: "1.5rem 0",
	},

	footerNote: {
		fontSize: "12px",
		opacity: 0.75,
	},

	centerRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},

	spinner: {
		width: "28px",
		height: "28px",
		border: "3px solid rgba(255,255,255,0.25)",
		borderTopColor: "white",
		borderRadius: "50%",
		animation: "spin 1s linear infinite",
	},

	vignette: {
		pointerEvents: "none",
		position: "fixed",
		inset: 0,
		background: "radial-gradient(80% 60% at 50% 40%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 100%)",
	},
};

// quick keyframes injection for the spinner (no CSS file needed)
const styleEl = document.getElementById("mc-spin-keyframes");
if (!styleEl) {
	const el = document.createElement("style");
	el.id = "mc-spin-keyframes";
	el.innerHTML = `
    @keyframes spin { to { transform: rotate(360deg); } }
  `;
	document.head.appendChild(el);
}

export default MeetingConfirmationPage;
